import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import React from "react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Mail, Phone, HelpCircle, CheckCircle, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import type { User } from "@supabase/supabase-js";
import SEO from "@/components/SEO";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const kontaktSchema = z.object({
  vorname: z.string().min(2, "Vorname muss mindestens 2 Zeichen lang sein").max(100),
  nachname: z.string().min(2, "Nachname muss mindestens 2 Zeichen lang sein").max(100),
  email: z.string().email("Bitte gib eine gültige E-Mail-Adresse ein"),
  telefon: z.string().min(5, "Bitte gib eine gültige Telefonnummer ein").max(20),
  betreff: z.string().min(3, "Betreff muss mindestens 3 Zeichen lang sein").max(200),
  grund: z.string().min(1, "Bitte wähle einen Grund aus"),
  nachricht: z.string().min(10, "Nachricht muss mindestens 10 Zeichen lang sein").max(2000),
});

type KontaktFormValues = z.infer<typeof kontaktSchema>;

const Kontakt = () => {
  const { toast } = useToast();
  const [showSuccessDialog, setShowSuccessDialog] = React.useState(false);
  const [user, setUser] = React.useState<User | null>(null);
  
  const form = useForm<KontaktFormValues>({
    resolver: zodResolver(kontaktSchema),
    defaultValues: {
      vorname: "",
      nachname: "",
      email: "",
      telefon: "",
      betreff: "",
      grund: "",
      nachricht: "",
    },
  });

  // Auth-Status prüfen
  React.useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // E-Mail vorausfüllen wenn eingeloggt
  React.useEffect(() => {
    if (user?.email) {
      form.setValue('email', user.email);
    }
  }, [user, form]);

  const onSubmit = async (data: KontaktFormValues) => {
    try {
      toast({
        title: "Nachricht wird gesendet...",
        description: "Bitte warte einen Moment.",
      });

      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: data,
      });

      if (error) throw error;

      setShowSuccessDialog(true);
      form.reset();
    } catch (error) {
      console.error("Fehler beim Senden:", error);
      toast({
        title: "Fehler beim Senden",
        description: "Bitte versuche es später erneut.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-accent/20">
      <SEO title={`Kontakt – Beratung & Support | MusiKonzept`} description={`Kontaktiere MusiKonzept für Fragen zu Mitgliedschaft, Ausbildungen oder Inhouse-Schulungen. Wir helfen dir persönlich weiter.`} path="/kontakt" />
      <Header />
      <main className="pt-20">
        {/* Success Dialog */}
        <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center animate-scale-in">
                  <CheckCircle className="w-10 h-10 text-primary animate-pulse" />
                </div>
              </div>
              <AlertDialogTitle className="text-center text-2xl">
                Nachricht erfolgreich versendet!
              </AlertDialogTitle>
              <AlertDialogDescription className="text-center space-y-4 pt-2">
                <p className="text-base">
                  Vielen Dank für deine Nachricht! Wir haben sie erhalten und werden uns schnellstmöglich per E-Mail bei dir zurückmelden.
                </p>
                <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-left">
                      <p className="font-semibold mb-1">Brauchst du schnelle Antworten?</p>
                      <p className="text-muted-foreground">
                        Besuche unsere Hilfeseite für häufig gestellte Fragen und detaillierte Informationen.
                      </p>
                    </div>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
              <Link to="/hilfe" className="flex-1">
                <Button variant="outline" className="w-full">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Zur Hilfeseite
                </Button>
              </Link>
              <AlertDialogAction className="flex-1">
                Verstanden
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Hero Section */}
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent animate-gradient">
              Kontakt
            </h1>
            <p className="text-xl text-muted-foreground">
              Wir freuen uns auf deine Nachricht! Fülle einfach das Formular aus und wir melden uns schnellstmöglich bei dir.
            </p>
          </div>

          {/* Hilfe-Hinweis */}
          <div className="max-w-3xl mx-auto mb-8">
            <Card className="p-6 bg-accent/10 border-accent/20 backdrop-blur-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-accent/20 rounded-full">
                  <HelpCircle className="w-6 h-6 text-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2">Suchst du Hilfe?</h3>
                  <p className="text-muted-foreground mb-3">
                    In unserem Hilfebereich findest du Antworten auf häufig gestellte Fragen und detaillierte Informationen zu unseren Angeboten.
                  </p>
                  <Link to="/hilfe">
                    <Button variant="outline" className="group">
                      Zur Hilfeseite
                      <HelpCircle className="ml-2 w-4 h-4 group-hover:rotate-12 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </div>

          {/* Kontaktformular */}
          <div className="max-w-3xl mx-auto">
            <Card className="p-8 bg-card/80 backdrop-blur-sm border-border/50 shadow-xl hover:shadow-2xl transition-all duration-500 animate-fade-in">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Vorname & Nachname */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="vorname"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Vorname *</FormLabel>
                          <FormControl>
                            <Input placeholder="Max" {...field} className="transition-all duration-300 focus:scale-[1.02]" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="nachname"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nachname *</FormLabel>
                          <FormControl>
                            <Input placeholder="Mustermann" {...field} className="transition-all duration-300 focus:scale-[1.02]" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* E-Mail & Telefon */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>E-Mail-Adresse *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                              <Input 
                                placeholder="max@beispiel.de" 
                                {...field} 
                                disabled={!!user}
                                className={cn(
                                  "pl-10 transition-all duration-300",
                                  user ? "bg-muted cursor-not-allowed opacity-70 pr-10" : "focus:scale-[1.02]"
                                )}
                              />
                              {user && (
                                <Lock className="absolute right-3 top-3 w-4 h-4 text-muted-foreground" />
                              )}
                            </div>
                          </FormControl>
                          {user && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Deine Account-E-Mail wird automatisch verwendet
                            </p>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="telefon"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Telefonnummer *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Phone className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                              <Input placeholder="0123 456789" {...field} className="pl-10 transition-all duration-300 focus:scale-[1.02]" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Betreff */}
                  <FormField
                    control={form.control}
                    name="betreff"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Betreff *</FormLabel>
                        <FormControl>
                          <Input placeholder="Worum geht es?" {...field} className="transition-all duration-300 focus:scale-[1.02]" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Grund der Kontaktaufnahme */}
                  <FormField
                    control={form.control}
                    name="grund"
                    render={({ field }) => (
                        <FormItem>
                          <FormLabel>Grund der Kontaktaufnahme *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="transition-all duration-300 focus:scale-[1.02]">
                                <SelectValue placeholder="Bitte wähle einen Grund aus" />
                              </SelectTrigger>
                            </FormControl>
                          <SelectContent className="bg-card z-50">
                            <SelectItem value="allgemeine-frage">Allgemeine Frage</SelectItem>
                            <SelectItem value="frage-mitgliedschaft">Frage zur Mitgliedschaft</SelectItem>
                            <SelectItem value="frage-musik-kita">Frage zur Musik-Kita</SelectItem>
                            <SelectItem value="frage-ausbildung">Frage zur Ausbildung</SelectItem>
                            <SelectItem value="frage-shop">Frage zum Shop</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Nachricht */}
                  <FormField
                    control={form.control}
                    name="nachricht"
                    render={({ field }) => (
                        <FormItem>
                          <FormLabel>Deine Nachricht *</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Teile uns dein Anliegen mit..." 
                              className="min-h-[150px] transition-all duration-300 focus:scale-[1.01]" 
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                    )}
                  />

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-gradient-to-r from-primary to-accent hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group"
                  >
                    Nachricht absenden
                    <Mail className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </form>
              </Form>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Kontakt;
